import styled from '@emotion/styled'
import { CATALOG_ITEM_TYPE, CatalogItemType } from '@pubstack/common/src/catalogItem'
import React, { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { BorderRadius } from '~/assets/style/tokens'
import { AdmIntegrationLogo } from '~/components/AdmIntegrationLogo'
import { Skeleton } from '~/components/Skeleton'
import { Tooltip } from '~/components/Tooltip'
import { WithClassName } from '~/types/utils'

const ModuleInformation = styled.div`
  display: flex;
  gap: 1.25rem;
  & > div {
    ${BorderRadius.style}
  }
`

const MODULE_LOGO_WIDTH = 286 //px

const ModuleLogo = styled.div`
  background-color: ${Colors.White};
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  max-width: ${MODULE_LOGO_WIDTH}px;
  flex: 0 0 ${MODULE_LOGO_WIDTH}px;
  & img {
    align-self: center;
    max-height: 50px;
    max-width: calc(${MODULE_LOGO_WIDTH}px - 1rem);
    width: auto;
  }
`

const ModuleDetail = styled.div`
  background-color: ${Colors.White};
  width: 100%;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1.25rem;
`

const ModuleDetailContent = styled.div`
  display: flex;
  gap: 0.625rem;
  column-gap: 2rem;
  flex-wrap: wrap;
`

const ModuleDetailLabel = styled.div`
  color: ${Colors.SlateGrey};
  ${Fonts.P2}
  display: flex;
  align-items:center;
  gap: 4px;
`

export type ExtraCatalogItemHeaderItem = {
  label: string
  value: string | React.ReactNode
  labelInfo?: React.ReactNode
  /** Opt-in, for an item that wants placing rather than the next slot in the row — `ml-auto` to end it. */
  className?: string
}
type ModuleHeaderProps = WithClassName & {
  catalogItemCode?: string
  isLoading: boolean
  catalogItemType: CatalogItemType
  status?: React.ReactNode
  baseCDNUrl: string
  extraHeaderItems?: ExtraCatalogItemHeaderItem[]
  /**
   * The company as the catalog names it, shown above the rest of the header.
   *
   * Pages whose logo is the whole answer — Prebid, GAM, TAM — leave it out and stay as they were. A bidder
   * seat is the case that needs it: the logo is the only thing naming whose seat this is, and a logo that
   * failed to load, or a bidder that never had one, leaves the page anonymous.
   */
  name?: string
  /**
   * What to print under "Integration type", where the catalog item's own kind is not the answer.
   *
   * The pages that list Prebid modules or ad servers want exactly what `catalogItemType` derives. A bidder
   * seat does not: every seat on that page is a `prebidBidder`, so the derived value was a constant, while
   * the thing the reader is actually after — client-side, server-side, Spark — was nowhere on the header.
   */
  integrationType?: React.ReactNode
}

const BaseModuleHeader: FunctionComponent<ModuleHeaderProps> = ({ isLoading, catalogItemCode, status, catalogItemType, baseCDNUrl, extraHeaderItems, name, integrationType }) => {
  return (
    <ModuleInformation>
      <ModuleLogo>{isLoading || !catalogItemCode ? <Skeleton bigger width={'195px'} /> : <AdmIntegrationLogo width={'132px'} name={catalogItemCode} baseCDNUrl={baseCDNUrl} />}</ModuleLogo>
      <ModuleDetail>
        <ModuleDetailContent>
          {name && (
            <div className={'w-full'}>
              {/* The adapter code is what the rest of the app keys this seat on and what support asks for,
                  so it stays within reach of the name rather than being the name. */}
              <Tooltip title={catalogItemCode ? `Adapter: ${catalogItemCode}` : ''}>
                <span className={'text-base font-medium text-ink'}>{isLoading ? <Skeleton width={'140px'} /> : name}</span>
              </Tooltip>
            </div>
          )}
          <div>
            <ModuleDetailLabel>Status</ModuleDetailLabel>
            <div>{isLoading ? <Skeleton width={'70px'} /> : status}</div>
          </div>
          <div>
            <ModuleDetailLabel>Integration type</ModuleDetailLabel>
            <span>{isLoading ? <Skeleton width={'70px'} /> : (integrationType ?? CATALOG_ITEM_TYPE[catalogItemType])}</span>
          </div>
          {extraHeaderItems?.map(({ label, value, labelInfo, className }) => (
            <div key={label} className={className}>
              <ModuleDetailLabel>
                {label}
                {labelInfo}
              </ModuleDetailLabel>
              <div>{value}</div>
            </div>
          ))}
        </ModuleDetailContent>
      </ModuleDetail>
    </ModuleInformation>
  )
}

export const ModuleHeader = styled(BaseModuleHeader)``
